/**
 * FamilyBookViewer.jsx — react-pageflip powered book viewer for a
 * rasterised family PDF. Loads all page URLs from the family
 * documents API and renders a two-page spread with real page-turn
 * animation, keyboard navigation, and a thumbnail strip.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, Loader2, ArrowLeft, Maximize2, Minimize2 } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const Page = React.forwardRef(function Page({ src, index, total }, ref) {
  // Detect landscape-oriented source pages (naturalWidth > naturalHeight).
  // The rasterised PDF preserves the original page orientation, so a
  // landscape page ends up as a wide image squeezed into our portrait
  // book slot. Rotate 90° so the content fills the slot naturally.
  const [rotate, setRotate] = useState(0);
  const [manualOverride, setManualOverride] = useState(false);

  const onImgLoad = (e) => {
    if (manualOverride) return;
    const el = e.currentTarget;
    if (el.naturalWidth > el.naturalHeight * 1.15) setRotate(90);
    else setRotate(0);
  };

  const cycleRotation = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    setManualOverride(true);
    setRotate((r) => (r + 90) % 360);
  };

  // When rotated 90/270, the image's natural landscape dimensions need
  // to swap to fit the portrait slot. We use CSS aspect-ratio + object-
  // contain via a wrapper that itself gets rotated so the browser
  // naturally handles the sizing.
  const isRotated = rotate === 90 || rotate === 270;

  return (
    <div
      ref={ref}
      className="bg-[#FAF6EF] shadow-inner overflow-hidden select-none relative"
      data-testid={`book-page-${index + 1}`}
    >
      <div className="h-full w-full flex flex-col">
        <div className="flex-1 flex items-center justify-center bg-[#F5EFE1] overflow-hidden relative">
          {src ? (
            <>
              <img
                src={resolveUrl(src)}
                alt={`Page ${index + 1}`}
                onLoad={onImgLoad}
                className="object-contain transition-transform duration-300"
                style={
                  isRotated
                    ? {
                        // When rotated, swap effective dims: constrain the
                        // pre-rotation width to slot HEIGHT and pre-rotation
                        // height to slot WIDTH via aspect-ratio compensation.
                        // Using `min(100vw, 100vh)` unreliable inside flex,
                        // so we cap generously and rely on object-contain.
                        maxWidth:  "calc(100% + 40vh)",
                        maxHeight: "88%",
                        height:    "88%",
                        width:     "auto",
                        transform: `rotate(${rotate}deg)`,
                        transformOrigin: "center",
                      }
                    : {
                        maxWidth:  "100%",
                        maxHeight: "100%",
                        transform: rotate ? `rotate(${rotate}deg)` : undefined,
                      }
                }
                draggable={false}
                data-rotated={rotate ? "true" : "false"}
              />
              <button
                onClick={cycleRotation}
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs opacity-0 hover:opacity-100 group-hover:opacity-100 transition"
                title="Rotate page 90°"
                data-testid={`book-page-rotate-${index + 1}`}
              >
                ⟳
              </button>
            </>
          ) : (
            <Loader2 className="w-6 h-6 animate-spin opacity-50" />
          )}
        </div>
        <div className="text-[10px] font-mono text-center py-1 opacity-50 bg-[#EDE4CE] border-t border-[#DBCFA4]">
          {index + 1} / {total}
        </div>
      </div>
    </div>
  );
});

export default function FamilyBookViewer() {
  const { treeSlug, docId } = useParams();
  const nav = useNavigate();
  const bookRef = useRef(null);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/family-trees/${treeSlug}/documents/${docId}`);
        setDoc(data);
        document.title = `${data.title} — Family Book`;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load book");
        nav(`/family/${treeSlug}/manage?tab=documents`);
      } finally { setLoading(false); }
    })();
  }, [treeSlug, docId, nav]);

  useEffect(() => {
    const onKey = (e) => {
      if (!bookRef.current?.pageFlip) return;
      if (e.key === "ArrowLeft")  bookRef.current.pageFlip().flipPrev();
      if (e.key === "ArrowRight") bookRef.current.pageFlip().flipNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pages = useMemo(() => doc?.page_urls || [], [doc]);
  const total = pages.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!doc) return null;

  const flipTo = (n) => bookRef.current?.pageFlip()?.flip(n);

  return (
    <div
      className={
        "min-h-screen flex flex-col " +
        (isFullscreen ? "fixed inset-0 z-50 bg-[#1a1210]" : "bg-gradient-to-br from-[#2d1e17] via-[#1f1611] to-[#2d1e17]")
      }
      data-testid="family-book-viewer"
    >
      {/* Top bar */}
      <div className="px-4 py-3 flex items-center gap-3 text-[#F5EFE1]">
        <Link
          to={`/family/${treeSlug}/manage?tab=documents`}
          className="inline-flex items-center gap-1 text-sm hover:underline"
          data-testid="book-back"
        >
          <ArrowLeft className="w-4 h-4" /> Documents
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="book-title">{doc.title}</div>
          {doc.description && (
            <div className="text-xs opacity-70 truncate">{doc.description}</div>
          )}
        </div>
        <div className="text-xs font-mono opacity-70" data-testid="book-progress">
          Page {page + 1} / {total}
        </div>
        <button
          onClick={() => setIsFullscreen((v) => !v)}
          className="p-1.5 rounded hover:bg-white/10"
          title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          data-testid="book-fullscreen"
        >
          {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Book */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div className="relative">
          <HTMLFlipBook
            ref={bookRef}
            width={720}
            height={980}
            size="stretch"
            minWidth={400}
            maxWidth={1100}
            minHeight={550}
            maxHeight={1500}
            maxShadowOpacity={0.5}
            showCover={true}
            mobileScrollSupport={true}
            useMouseEvents={true}
            onFlip={(e) => setPage(e.data)}
            className="drop-shadow-2xl"
            style={{}}
            startPage={0}
            drawShadow={true}
            flippingTime={700}
            usePortrait={true}
            startZIndex={0}
            autoSize={true}
            clickEventForward={true}
            swipeDistance={30}
            showPageCorners={true}
            disableFlipByClick={false}
          >
            {pages.map((src, i) => (
              <Page key={i} src={src} index={i} total={total} />
            ))}
          </HTMLFlipBook>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 flex items-center gap-3 text-[#F5EFE1] border-t border-white/10">
        <button
          onClick={() => bookRef.current?.pageFlip()?.flipPrev()}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm"
          data-testid="book-prev"
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <input
          type="number"
          min={1}
          max={total}
          value={page + 1}
          onChange={(e) => {
            const n = Math.max(1, Math.min(total, Number(e.target.value) || 1)) - 1;
            flipTo(n);
          }}
          className="w-20 rounded-full bg-white/10 text-center py-1.5 text-sm outline-none"
          data-testid="book-page-jump"
        />
        <span className="text-xs opacity-60">of {total}</span>
        <div className="flex-1" />
        <button
          onClick={() => bookRef.current?.pageFlip()?.flipNext()}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm"
          data-testid="book-next"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Thumbnail strip */}
      <div
        className="px-4 py-2 flex gap-1 overflow-x-auto bg-black/30 border-t border-white/10"
        style={{ scrollBehavior: "smooth" }}
        data-testid="book-thumbs"
      >
        {pages.map((src, i) => (
          <button
            key={i}
            onClick={() => flipTo(i)}
            className={
              "shrink-0 border rounded overflow-hidden transition " +
              (i === page ? "ring-2 ring-amber-300 border-amber-300" : "border-white/20 opacity-60 hover:opacity-100")
            }
            style={{ width: 34, height: 46 }}
            title={`Page ${i + 1}`}
            data-testid={`book-thumb-${i + 1}`}
          >
            <img src={resolveUrl(src)} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
}
